import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import EmptyState from '@/components/base/EmptyState';
import { newsList, newsSections } from '@/mocks/news';
import { formatDate } from '@/lib/queries';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 5;

export default function NewsPage() {
  const [section, setSection] = useState('全部');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const sorted = useMemo(() => [...newsList].sort((a, b) => b.date.localeCompare(a.date)), []);
  const result = useMemo(
    () => (section === '全部' ? sorted : sorted.filter((item) => item.section === section)),
    [section, sorted],
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [section]);

  useSeo({
    title: '影视资讯 - 行业观察与新片速递 - 日韩成人电影',
    description:
      '日韩成人电影的影视资讯栏目，提供行业观察、新片速递、片场手记与榜单数据，帮助成年观众了解日韩影视圈的最新动态与幕后故事。',
    keywords: '日韩成人电影资讯,日韩影视资讯,行业观察,新片速递,片场手记,榜单数据',
  });

  const [featured, ...rest] = result;
  const restShown = rest.slice(0, Math.max(visible - 1, 0));

  return (
    <>
      <PageHeader
        eyebrow="Industry news"
        title="影视资讯"
        description="行业观察、新片速递、片场手记与榜单数据。我们用可核查的方式记录日韩影视圈正在发生的事，不写标题党，也不做夸大宣传。"
        crumbs={[{ label: '影视资讯' }]}
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <i className="ri-article-line text-sm text-primary-300" />共 {newsList.length} 篇文章
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm text-primary-300" />
              最近更新 {formatDate(sorted[0]?.date ?? '')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-sm text-primary-300" />4 个栏目
            </span>
          </>
        }
      />

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
          {['全部', ...newsSections].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setSection(item)}
              className={`shrink-0 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                section === item
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'border border-background-800 bg-background-900 text-foreground-400 hover:border-background-600 hover:text-foreground-100'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {result.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              icon="ri-article-line"
              title="这个栏目暂时还没有文章"
              description="编辑正在整理新的内容，你可以先切换到其他栏目，或者返回首页浏览影片推荐。"
              onReset={() => setSection('全部')}
              resetLabel="查看全部资讯"
              primaryLabel="返回首页"
              primaryTo="/"
            />
          </div>
        ) : (
          <div className="mt-8">
            {featured && (
              <Link
                to={`/news/${featured.id}`}
                className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-colors hover:border-primary-600 md:grid-cols-[1.15fr_1fr]"
              >
                <div className="relative h-[220px] w-full overflow-hidden bg-background-800 md:h-full md:min-h-[280px]">
                  <img
                    src={featured.cover}
                    alt={`${featured.title} 配图`}
                    title={`${featured.title} - 日韩成人电影资讯`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <span className="absolute left-3 top-3 rounded bg-primary-500 px-2 py-1 text-[11px] text-background-50">
                    置顶 · {featured.section}
                  </span>
                </div>
                <div className="flex flex-col justify-center p-5 md:p-8">
                  <div className="flex items-center gap-2 text-[11px] text-foreground-600">
                    <span className="font-label tracking-[0.12em]">{formatDate(featured.date)}</span>
                    <span className="h-2.5 w-px bg-background-700" />
                    <span>{featured.author}</span>
                  </div>
                  <h2 className="mt-3 text-xl font-bold leading-snug text-foreground-50 transition-colors group-hover:text-accent-400 md:text-2xl">
                    {featured.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-400">{featured.summary}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    {featured.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-background-800 px-2.5 py-1 text-[11px] text-foreground-400">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs text-foreground-300 transition-colors group-hover:text-accent-400">
                    阅读全文
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </Link>
            )}

            <div className="mt-5 divide-y divide-background-800 border-y border-background-800">
              {restShown.map((article) => (
                <Link key={article.id} to={`/news/${article.id}`} className="group flex cursor-pointer gap-4 py-5">
                  <div className="h-[86px] w-[120px] shrink-0 overflow-hidden rounded bg-background-800 sm:h-[104px] sm:w-[150px]">
                    <img
                      src={article.cover}
                      alt={`${article.title} 配图`}
                      title={`${article.title} - 日韩成人电影资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-600">
                      <span className="rounded bg-background-800 px-1.5 py-0.5 text-foreground-400">{article.section}</span>
                      <span className="font-label tracking-[0.12em]">{formatDate(article.date)}</span>
                      <span className="hidden h-2.5 w-px bg-background-700 sm:block" />
                      <span className="hidden truncate sm:block">{article.author}</span>
                    </div>
                    <h3 className="mt-2 text-sm font-semibold leading-relaxed text-foreground-100 transition-colors group-hover:text-accent-400 md:text-base">
                      {article.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">{article.summary}</p>
                  </div>
                  <i className="mt-2 hidden shrink-0 self-start text-lg text-foreground-700 transition-colors group-hover:text-accent-500 sm:block ri-arrow-right-line" />
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
              <p className="text-xs text-foreground-600">
                已显示 {Math.min(visible, result.length)} / {result.length} 篇文章
              </p>
              {visible < result.length && (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-700 px-6 py-2.5 text-sm text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  加载更多资讯
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}